import { portfolioData } from 'sections/Portfolio';
import { gamesData } from 'sections/Games';
import { toolsData } from 'sections/Tools';
import { tutorialsData } from 'sections/Tutorials';

export type RouteMeta = {
  title: string;
  description: string;
  path: string;
  type?: 'website' | 'article' | 'profile';
  keywords?: string[];
  image?: string;
};

export const siteConfig = {
  siteName: 'Hardesty.ai',
  author: 'Ryan Hardesty Lewis',
  baseUrl: 'https://hardesty.ai',
  defaultDescription:
    'Digital twins, AI-powered civic tools, and playful experiments by Ryan Hardesty Lewis.',
  defaultImage: '/assets/images/Picture1.png',
  lightThemeColor: '#ffffff',
  darkThemeColor: '#030712',
};

const registeredRoutes = [
  '/',
  '/about',
  '/press',
  '/publications',
  '/contact',
  '/login',
  '/edit',
  '/projects/voxel-earth',
  '/projects/fire-smoke-digital-twin',
  '/projects/crowdsourced-streetview',
  '/projects/treefolio',
  '/projects/computegpt',
  '/projects/austin-digital-twin',
  '/projects/recyclar',
  '/projects/ut-austin-3d-virtual-tour',
  '/projects/heat-risk-dashboard',
  '/projects/ai-feed',
  '/projects/central-documentation',
  '/projects/city-contributor',
  '/projects/urban-tessellation',
  '/projects/gitchat',
  '/projects/indoor-temperature',
  '/projects/datajourney',
  '/projects/llm-compression',
  '/projects/transfer-learning',
  '/projects/meshtorrent',
  '/projects/rigtorrent',
  '/projects/ethical-games',
  '/tools/wix-scraper',
  '/tools/acrc',
  '/tools/update-launcher',
  '/tools/gpt-auto-scraper',
  '/tools/relay-server',
  '/tools/monstera-login-menu',
  '/tools/duckduckgo-image-scraper',
  '/tools/bingai-unofficial-api',
  '/tools/serverless-portfolio',
  '/games/princess-and-protector',
  '/games/social-puzzle',
  '/games/time-trodden',
  '/games/good-knight',
  '/games/hallowed-island',
  '/games/another-one',
  '/tutorials/stable-diffusion-in-unity',
  '/tutorials/physics-3d-in-unity',
  '/tutorials/realm-of-the-mad-frog',
  '/tutorials/generative-dialogue',
  '/tutorials/volition',
  '/tutorials/websocket-multiplayer',
  '/tutorials/door-interaction',
] as const;

const registeredSet = new Set<string>(registeredRoutes);

const ensureLeadingSlash = (value?: string) => {
  if (!value) {
    return undefined;
  }
  return value.startsWith('/') ? value : `/${value}`;
};

const normalizePath = (value: string) => {
  if (!value.startsWith('/')) {
    value = `/${value}`;
  }
  if (value.length > 1 && value.endsWith('/')) {
    return value.slice(0, -1);
  }
  return value || '/';
};

const buildProjectMeta = () =>
  Object.fromEntries(
    portfolioData
      .map((item) => {
        const path = normalizePath(item.link);
        if (!registeredSet.has(path)) {
          return null;
        }
        return [
          path,
          {
            path,
            title: `${item.title} - Hardesty.ai`,
            description: item.description,
            type: 'article',
            keywords: [
              item.title,
              item.category,
              'digital twins',
              'Ryan Hardesty Lewis',
            ],
            image: ensureLeadingSlash(item.image),
          } satisfies RouteMeta,
        ];
      })
      .filter(Boolean) as Array<[string, RouteMeta]>
  );

const buildToolsMeta = () =>
  Object.fromEntries(
    toolsData
      .map((item) => {
        const path = normalizePath(item.link);
        if (!registeredSet.has(path)) {
          return null;
        }
        return [
          path,
          {
            path,
            title: `${item.title} Tool - Hardesty.ai`,
            description: item.description,
            type: 'article',
            keywords: [
              item.title,
              item.category,
              'developer tools',
              'Ryan Hardesty Lewis',
            ],
            image: ensureLeadingSlash(item.image),
          } satisfies RouteMeta,
        ];
      })
      .filter(Boolean) as Array<[string, RouteMeta]>
  );

const buildGamesMeta = () =>
  Object.fromEntries(
    gamesData
      .map((item) => {
        const path = normalizePath(item.link);
        if (!registeredSet.has(path)) {
          return null;
        }
        return [
          path,
          {
            path,
            title: `${item.title} Game - Hardesty.ai`,
            description: item.description,
            type: 'article',
            keywords: [item.title, item.category, 'indie games', 'Ryan Hardesty Lewis'],
            image: ensureLeadingSlash(item.image),
          } satisfies RouteMeta,
        ];
      })
      .filter(Boolean) as Array<[string, RouteMeta]>
  );

const buildTutorialMeta = () =>
  Object.fromEntries(
    tutorialsData
      .map((item) => {
        const path = normalizePath(item.link);
        if (!registeredSet.has(path)) {
          return null;
        }
        return [
          path,
          {
            path,
            title: `${item.title} Tutorial - Hardesty.ai`,
            description: item.description,
            type: 'article',
            keywords: [
              item.title,
              item.category,
              'game tutorial',
              'Ryan Hardesty Lewis',
            ],
            image: ensureLeadingSlash(item.image),
          } satisfies RouteMeta,
        ];
      })
      .filter(Boolean) as Array<[string, RouteMeta]>
  );

const staticMeta: Record<string, RouteMeta> = {
  '/': {
    path: '/',
    title: 'Hardesty.ai | Digital twins, AI, and playful research',
    description:
      'Explore the portfolio of Ryan Hardesty Lewis: immersive digital twins, AI civic tech, indie games, tutorials, and tools built to make cities smarter.',
    type: 'website',
    keywords: [
      'Hardesty.ai',
      'Ryan Hardesty Lewis',
      'digital twins',
      'AI research',
      'urban tech',
    ],
    image: ensureLeadingSlash('/assets/images/voxel-earth.png'),
  },
  '/about': {
    path: '/about',
    title: 'About Ryan Hardesty Lewis - Hardesty.ai',
    description:
      'Meet Ryan Hardesty Lewis, an MIT PhD student building equitable cities through digital twins, climate resilience research, and community-forward technology.',
    type: 'profile',
    keywords: [
      'Ryan Hardesty Lewis biography',
      'MIT PhD urban informatics',
      'Hardesty.ai',
    ],
  },
  '/press': {
    path: '/press',
    title: 'Press Highlights - Hardesty.ai',
    description:
      'Coverage from SIGGRAPH, BBC, PC Gamer, Fox, and more on projects like Voxel Earth, Treefolio, and AI-powered civic tech by Ryan Hardesty Lewis.',
    type: 'website',
    keywords: [
      'Hardesty.ai press',
      'Voxel Earth news',
      'Treefolio press',
      'Ryan Hardesty Lewis interviews',
    ],
  },
  '/publications': {
    path: '/publications',
    title: 'Publications & Talks - Hardesty.ai',
    description:
      'Peer-reviewed papers, workshops, and talks on digital twins, AI for public systems, and generative experiences authored by Ryan Hardesty Lewis.',
    type: 'article',
    keywords: [
      'Ryan Hardesty Lewis publications',
      'digital twin research',
      'AI civic tech papers',
    ],
  },
  '/contact': {
    path: '/contact',
    title: 'Connect with Ryan - Hardesty.ai',
    description:
      'Book time with Ryan Hardesty Lewis, explore collaboration opportunities, or reach out via email to talk digital twins, AI, and resilient cities.',
    type: 'website',
    keywords: ['contact Ryan Hardesty Lewis', 'Hardesty.ai Calendly'],
  },
  '/login': {
    path: '/login',
    title: 'Admin Login - Hardesty.ai',
    description:
      'Secure portal for the Hardesty.ai content system. Authenticates trusted collaborators to update portfolio content.',
    type: 'website',
    keywords: ['Hardesty.ai admin', 'Ryan Hardesty Lewis login'],
  },
  '/edit': {
    path: '/edit',
    title: 'Browser Editor - Hardesty.ai',
    description:
      'In-browser TSX editor backed by GitHub for updating the Hardesty.ai experience with live preview and instant deploys.',
    type: 'website',
    keywords: ['Hardesty.ai editor', 'Ryan Hardesty Lewis CMS'],
  },
};

const routeMeta: Record<string, RouteMeta> = {
  ...buildProjectMeta(),
  ...buildToolsMeta(),
  ...buildGamesMeta(),
  ...buildTutorialMeta(),
  ...staticMeta,
};

registeredRoutes.forEach((path) => {
  if (!routeMeta[path]) {
    routeMeta[path] = {
      path,
      title: `${siteConfig.siteName} - ${path.replace('/', '') || 'page'}`,
      description: siteConfig.defaultDescription,
      type: 'website',
      keywords: [siteConfig.siteName, siteConfig.author],
    };
  }
});

export const getRouteMeta = (pathname: string): RouteMeta => {
  const normalized = normalizePath(pathname || '/');
  return routeMeta[normalized] ?? {
    path: normalized,
    title: `${siteConfig.siteName} | ${siteConfig.author}`,
    description: siteConfig.defaultDescription,
    type: 'website',
    keywords: [siteConfig.siteName, siteConfig.author],
  };
};
